import React, { useState } from 'react';
import PageSection from '../components/PageSection';
import PrimaryButtonLink from '../components/PrimaryButtonLink';

const initialForm = {
  apiKey: '',
  message: '',
  mode: 'balanced'
};

const modeLabels = {
  balanced: 'Bilanciata',
  strict: 'Rigorosa',
  permissive: 'Permissiva'
};

const PrivaPrompt = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(current => ({
      ...current,
      [name]: value
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setStatus('loading');
    setResult(null);
    setErrorMessage('');

    try {
      const response = await fetch('http://localhost:3100/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: form.message,
          apiKey: form.apiKey,
          mode: form.mode
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Errore sconosciuto');
      }

      setResult(data.data);
      setStatus('success');
    } catch (error) {
      setErrorMessage(error.message || 'Errore di connessione');
      setStatus('error');
    }
  };

  return (
    <>

      <PageSection
        title="PrivaPrompt: AI sicura per dati sensibili"
        intro="Porta l’intelligenza artificiale nei tuoi processi senza esporre informazioni critiche. PrivaPrompt anonimizza i contenuti prima dell’elaborazione, preserva il valore del prompt e ti aiuta a usare l’AI in modo piu sicuro, controllato e adatto al tuo settore."
      >
        <div className="prompt-top-grid">
          <article className="prompt-side-card">
            <h3>Proteggi i tuoi dati mentre usi l’AI</h3>
            <ul>
              <li>Invia qualsiasi messaggio al modello AI senza esporre informazioni sensibili.</li>
              <li>Il sistema anonimizza automaticamente i dati prima dell’elaborazione e ricostruisce la risposta finale in modo sicuro.</li>
            </ul>
          </article>

          <article className="prompt-side-card">
            <h3>Cosa ottieni</h3>
            <ul>
              <li>Risposta AI completa e leggibile</li>
              <li>Dati sensibili protetti automaticamente</li>
              <li>Contenuti elaborati senza rischio di esposizione</li>
              <li>Compatibile con OpenAI e altri modelli AI</li>
            </ul>
          </article>
        </div>

        <div className="prompt-why-section">
          <article className="prompt-side-card">
            <h3>Perché usarlo</h3>
            <div className="prompt-benefit-list">
              <div className="prompt-benefit-item">
                <span className="prompt-benefit-icon" aria-hidden="true">🛡️</span>
                <div>
                  <strong>Zero condivisioni accidentali</strong>
                  <p>Blocca l’invio non intenzionale di dati sensibili verso il modello.</p>
                </div>
              </div>

              <div className="prompt-benefit-item">
                <span className="prompt-benefit-icon" aria-hidden="true">🔍</span>
                <div>
                  <strong>Meno rischio di re-identificazione</strong>
                  <p>Riduce l’esposizione di dettagli che potrebbero ricondurre a persone o casi reali.</p>
                </div>
              </div>

              <div className="prompt-benefit-item">
                <span className="prompt-benefit-icon" aria-hidden="true">⚙️</span>
                <div>
                  <strong>Adattabile al tuo settore</strong>
                  <p>Policy e livelli di protezione personalizzabili per esigenze operative e conformità.</p>
                </div>
              </div>
            </div>

            <div className="prompt-demo-grid">
              <div className="prompt-demo-card">
                <span className="prompt-demo-kicker">Richiedi Demo verticale</span>
                <strong>Assicurazioni</strong>
                <p>Gestione sicura di dati cliente, pratiche e sinistri.</p>
              </div>
              <div className="prompt-demo-card">
                <span className="prompt-demo-kicker">Richiedi Demo verticale</span>
                <strong>Sanitario</strong>
                <p>Tutela di informazioni cliniche e dati altamente sensibili.</p>
              </div>
            </div>
          </article>
        </div>

        <div className="prompt-cta-panel">
          <div>
            <p className="eyebrow">Vuoi vederlo in azione?</p>
            <h3>Richiedi una demo guidata di PrivaPrompt.</h3>
            <p>
              Ti mostriamo come proteggere dati sensibili, adattare le policy al tuo
              settore e integrare il flusso nei tuoi processi.
            </p>
          </div>
          <PrimaryButtonLink to="/contatti">Chiedi una demo</PrimaryButtonLink>
        </div>

        <div className="prompt-layout prompt-layout-hidden">
          <div>
            <div className="prompt-form-shell">
              <form className="prompt-form" onSubmit={handleSubmit}>
                <label htmlFor="apiKey">
                  OpenAI API Key
                  <input
                    id="apiKey"
                    name="apiKey"
                    type="password"
                    value={form.apiKey}
                    onChange={handleChange}
                    placeholder="sk-..."
                    required
                  />
                </label>

                <label htmlFor="message">
                  Messaggio
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Scrivi il tuo messaggio qui..."
                    required
                  />
                </label>

                <label htmlFor="mode">
                  Modalita di sanitizzazione
                  <select id="mode" name="mode" value={form.mode} onChange={handleChange}>
                    {Object.entries(modeLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="prompt-submit-row">
                  <button
                    type="submit"
                    className="btn btn-primary prompt-submit"
                    disabled={status === 'loading'}
                  >
                    {status === 'loading' ? 'Invio in corso...' : 'Invia'}
                  </button>
                  
                </div>
              </form>
            </div>

            {status === 'loading' && (
              <div className="prompt-response loading">
                <h3>Elaborazione della richiesta...</h3>
                <p>Il proxy sta analizzando il prompt e inoltrando la richiesta al modello.</p>
              </div>
            )}

            {status === 'error' && (
              <div className="prompt-response error">
                <h3>Errore</h3>
                <p>{errorMessage}</p>
              </div>
            )}

            {status === 'success' && result && (
              <div className="prompt-response success">
                <h3>Risposta riuscita</h3>
                <p className="prompt-response-copy">{result.response}</p>

                <div className="prompt-response-grid">
                  <div className="prompt-metric">
                    <span className="prompt-metric-label">Prompt sanitizzato</span>
                    <p className="prompt-response-copy">{result.sanitizedPrompt}</p>
                  </div>
                  <div className="prompt-metric">
                    <span className="prompt-metric-label">Rischio rilevato</span>
                    <p>
                      {result.risk?.level || 'N/D'}{' '}
                      {typeof result.risk?.finalRisk !== 'undefined'
                        ? `(punteggio: ${result.risk.finalRisk})`
                        : ''}
                    </p>
                  </div>
                </div>

                <div className="prompt-metric">
                  <span className="prompt-metric-label">Entita rilevate</span>
                  {result.entities?.length ? (
                    <ul className="prompt-entity-list">
                      {result.entities.map((entity, index) => (
                        <li key={`${entity.type}-${entity.text}-${index}`}>
                          <strong>{entity.type}</strong>: "{entity.text}"
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Nessuna entita rilevata.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </PageSection>
    </>
  );
};

export default PrivaPrompt;
